import { useMutation, useQuery } from '@tanstack/react-query'
import { postNamedCommand, postNamedQuery } from '../service'
import { QUERY_KEYS } from './queryKeys'

/**
 * Hook to fetch a Plaid Link token via the Orion Plaid integration.
 *
 * @param {Object} params
 * @param {number} params.clientId - The client level ID
 * @param {string} [params.appName='Summit Wealth'] - App name shown in Plaid Link
 * @param {boolean} [params.isInform=false] - Whether this is an Inform (client-facing) request
 * @param {Object} [params.linkParams] - Additional optional params (itemID, authOnly, householdID, etc.)
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query
 * @returns {Object} React Query result with link token data
 */
export const useGetPlaidLinkToken = (params = {}, options = {}) => {
  const {
    clientId,
    appName = 'Summit Wealth',
    isInform = false,
    linkParams = {}
  } = params

  const { enabled = true } = options

  return useQuery({
    queryKey: [QUERY_KEYS.plaidLinkToken, clientId, appName],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'get-orion-plaid-data', {
        levelTypeId: 201,
        levelIds: [clientId],
        path: 'linkTokens',
        params: {
          appName,
          isInform,
          ...linkParams
        }
      })

      return data
    },
    enabled: enabled && !!clientId,
    staleTime: 5 * 60 * 1000,
    retry: 3
  })
}

/**
 * Mutation hook to complete the Plaid Link flow.
 * Sends the public token to the backend which handles:
 * 1. Exchanging the token for an Orion item
 * 2. Fetching account data
 * 3. Creating shell accounts
 * 4. Creating an integrationsLevels record for future polling
 *
 * @param {Object} params
 * @param {number} params.clientId - The client level ID
 * @param {boolean} [params.isInform=false] - Whether this is a client-facing request
 * @returns {Object} React Query mutation with mutate({ publicToken, institution })
 */
export const useCompletePlaidLink = (params = {}) => {
  const { clientId, isInform = false } = params

  return useMutation({
    mutationFn: async ({ publicToken, institution }) => {
      const { data } = await postNamedCommand('coreData', 'plaid-complete-link', {
        levelTypeId: 201,
        levelIds: [clientId],
        publicToken,
        isInform,
        institution
      })

      return data
    }
  })
}
